<template src="./ResetPinComponent.html"></template>
<!-- Add "scoped" attribute to limit CSS to this component only -->
<script>
import PinCodeComponent from '../PinCodeComponent/PinCodeComponent';
import VerifySmsComponent from '../VerifySmsComponent/VerifySmsComponent';
import { REQUIRED_PIN_LENGTH } from '@/constants';
import { SIGN_IN_RESPONSE_STATUS } from '@bnr-dev/auth-service/model/SignInResponse';
import SIGN_IN_STAGE from '@/model/SignInStage';
import CounterInformation from '../GlobalComponents/CounterInformation';
import {getSupportedBrowserLanguage} from '@bnr-dev/translate';

export default {
    components: {
        PinCodeComponent,
        VerifySmsComponent,
        CounterInformation
    },

    inject: ['authService'],
    props: {
        initialResetCode: {
            type: String,
            default: null
        },
        otcCode: {
            type: String,
            default: null
        },
        isAccountSetup: {
            type: Boolean,
            default: false
        },
        // eslint-disable-next-line vue/require-default-prop
        phone: {
            type: String
        }
    },
    data() {
        return {
            resetCode: null,
            emailSent: false,
            emailResult: false,
            email: null,
            newPin: '',
            repeatedPin: '',
            showLoadingSpinner: false,
            errorMessage: '',
            phoneMask: null,
            isLinkExpired: false
        };
    },
    computed: {
        getRequiredPinLength: () => REQUIRED_PIN_LENGTH
    },
    mounted() {
        if (this.initialResetCode) {
            this.resetCode = this.initialResetCode;
        } else {
            this.resetCode = this.$route.query.resetCode;
        }
        this.email = this.$route.query.email;
    },
    methods: {
        redirectToLogin() {
            this.$router.replace({ name: 'SignIn' });
        },

        onSubmitEmail: function () {
            const languageCode = getSupportedBrowserLanguage();
            this.showLoadingSpinner = true;
            this.authService
                .requestPinReset(this.email, languageCode)
                .then(() => {
                    this.emailSent = true;
                    this.emailResult = this.$t('reset-pin.email-sent-info');
                })
                .catch((error) => {
                    if (
                        error.json &&
                        error.json.errorCode &&
                        error.json.errorCode === 'ERROR_CODE_UNKNOWN_USER'
                    ) {
                        this.emailResult = this.$t('reset-pin.email-not-found');
                    } else {
                        this.errorMessage = this.$t(
                            'generic.unexpected-error-occurred'
                        );
                    }
                })
                .finally(() => {
                    this.showLoadingSpinner = false;
                });
        },
        onNewPinCodeInput(pinCode) {
            this.newPin = pinCode;
            if (this.newPin.length === REQUIRED_PIN_LENGTH) {
                this.$refs.repeatPinComponent.focusCellByIndex(0);
            }
            this.validatePins();
        },
        onRepeatNewPinCodeInput(pinCode) {
            this.repeatedPin = pinCode;
            this.validatePins();
        },
        submitNewPin: function () {
            if (
                this.newPin.length !== REQUIRED_PIN_LENGTH ||
                this.repeatedPin.length !== REQUIRED_PIN_LENGTH
            ) {
                this.errorMessage = this.$t('reset-pin.incomplete-pins');
                return;
            }
            if (!this.validatePins()) {
                return;
            }

            this.showLoadingSpinner = true;

            this.sendToApi()
                .then((signInResponse) => {
                    this.handleSignInResponse(signInResponse);
                })
                .catch((e) => {
                    if (String(e).includes('ERROR_CODE_INVALID_CODE')) {
                        this.isLinkExpired = true;
                    }
                    this.errorMessage = this.$t(
                        'generic.unexpected-error-occurred'
                    );
                })
                .finally(() => {
                    this.showLoadingSpinner = false;
                });
        },
        sendToApi() {
            const languageCode = getSupportedBrowserLanguage();
            if (this.isAccountSetup) {
                return this.authService.setupNewAccount(
                    this.email,
                    this.phone,
                    this.otcCode,
                    this.newPin,
                    this.resetCode,
                    languageCode
                );
            } else {
                return this.authService.resetPin(this.resetCode, this.newPin, languageCode);
            }
        },
        handleSignInResponse(signInResponse) {
            if (signInResponse.status === SIGN_IN_RESPONSE_STATUS.OTC_REQUIRED) {
                this.phoneMask = signInResponse.phoneMask;
                this.currentStage = SIGN_IN_STAGE.SMS;
            } else {
                this.redirectToLogin();
            }
        },
        validatePins() {
            this.errorMessage = '';
            if (
                this.newPin.length !== REQUIRED_PIN_LENGTH ||
                this.repeatedPin.length !== REQUIRED_PIN_LENGTH
            ) {
                return false;
            }
            if (this.newPin !== this.repeatedPin) {
                this.errorMessage = this.$t(
                    'reset-pin.validate-different-pins'
                );
                return false;
            }
            return true;
        }
    }
};
</script>

<style scoped lang="scss" src="./ResetPinComponent.scss"></style>
